import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import * as Util from '../../../common/utilities';
import { withSession } from '../../../i18n/containers/AppInit.js';
import { default as config } from '../../../utilities/config';

import {
  Card,
  Button,
  ButtonGroup,
  Dropdown,
  Input,
  InputIcon,
  MediaObject,
  Spinner,
  Toast,
  ToastContainer
} from '@salesforce/design-system-react';

import {
  AssetBrowser,
  AssetFilterPane,
  ArchiveFallbackModal,
  UploadCatalogHelper
} from '../index';
import { NoDataScreen } from '..';
import { withFlags } from '../../../flags/Flags';

import './AssetOverview.scss';
import moment from 'moment';

class AssetOverview extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      filterOpen: false,
      isLoadingAssets: true,
      scrollToTop: false,
      loadedOnce: false
    };
    this.typingTimer = null;
  }
  componentDidMount() {
    this.props.actions.getAssetSummary();
    this.props.actions.getAttributes();
    this.props.actions.getAssetTypes({
      callback: () =>
        this.props.actions.searchAssets(
          {},
          { page: 1 },
          { context: 'browser', mode: 'reset' }
        )
    });
  }

  hasMore() {
    return this.props.assets.search.browser?.paging?.more;
  }

  isPaging = false;

  handleLoadMore() {
    if (!this.isPaging) {
      this.isPaging = true;
      const currentPage = this.props.assets.search.browser.paging.page;
      if (currentPage) {
        this.props.actions.searchAssets(
          this.props.assets.browsedFilter,
          { page: currentPage + 1 },
          { context: 'browser' }
        );
      }
    }
    this.isPaging = true;
  }

  handleScrollToTop() {
    this.setState({ scrollToTop: false });
  }

  handleSort(sort) {
    this.props.actions.updateAssetFilter({ sortColumn: { ...sort } });
  }

  componentDidUpdate(prevProps) {
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'assets.search.browser.paging.page'
      )
    ) {
      this.isPaging = false;
    }

    if (
      Util.propChanged(
        prevProps,
        this.props,
        'assets.search.browser.assets.length'
      )
    ) {
      if (
        this.props.assets.search.browser?.assets?.length &&
        !this.state.loadedOnce
      ) {
        this.setState({ loadedOnce: true });
      }
    }

    // resettable changes will reset the current page to 1
    const filterChanges = {};
    ['assetClass', 'name', 'customFilters', 'sortColumn'].forEach(prop => {
      filterChanges[prop] = Util.propChanged(
        prevProps,
        this.props,
        `assets.browsedFilter.${prop}`
      );
    });

    // when a filter is first added but not confirmed, it is added with property === undefined
    // unconfirmed filter changes should not reset assets
    if (
      filterChanges.customFilters &&
      !filterChanges.name &&
      !filterChanges.assetClass &&
      !filterChanges.sortColumn
    ) {
      if (
        this.props.assets.browsedFilter.customFilters.length === 1 &&
        this.props.assets.browsedFilter.customFilters[0].property === undefined
      ) {
        filterChanges['customFilters'] = false;
      } else if (
        this.props.assets.browsedFilter.customFilters.length === 0 &&
        prevProps.assets.browsedFilter.customFilters.length === 1 &&
        prevProps.assets.browsedFilter.customFilters[0].property === undefined
      ) {
        filterChanges['customFilters'] = false;
      }
    }

    if (Object.values(filterChanges).some(change => change)) {
      this.setState({ scrollToTop: true });
      this.props.actions.searchAssets(
        this.props.assets.browsedFilter,
        { page: 1 },
        { context: 'browser', mode: 'reset' }
      );
    }

    if (Util.propChanged(prevProps, this.props, 'assets.lastSave')) {
      if (this.props.assets.lastSave) {
        // we need to get asset types here to have access to fallback_count at the right time
        this.props.actions.getAssetTypes();
        const { lastSave } = this.props.assets;
        if (lastSave.error) {
          this.props.actions.openToast('apiError', {
            ...lastSave.error
          });
        } else {
          this.props.actions.openToast('asset', {
            key: lastSave.asset.key,
            mode: lastSave.mode
          });
        }
      }
    }
  }

  assetOverviewHeader(paging) {
    const { browsedFilter } = this.props.assets;
    const { formatMessage } = this.props.intl;

    const count = paging?.total || 0;

    // item count
    const itemCount = formatMessage(
      { id: 'components.ecs.assetBrowser.header.items' },
      { count }
    );

    // all counters
    const counters = [itemCount];

    // filter count
    if (browsedFilter) {
      const assetFilterCount = browsedFilter.assetClass ? 1 : 0;
      let customFilterCount = 0;
      if (browsedFilter.customFilters) {
        customFilterCount = browsedFilter.customFilters.filter(
          filter => !!filter.property
        ).length;
      }
      const filterCount = customFilterCount + assetFilterCount;
      if (filterCount > 0) {
        counters.push(
          formatMessage(
            { id: 'components.ecs.assetBrowser.header.filters' },
            { count: filterCount }
          )
        );
      }
    }

    // provide the header body and join all counters together separated by bullets
    const body = (
      <div className="asset-overview-header">
        <h2>
          <FormattedMessage id="components.ecs.assetBrowser.header.title" />
        </h2>
        {counters.join(' • ')}
      </div>
    );
    return <MediaObject body={body} />;
  }

  onMenuSelect({ value }) {
    switch (value) {
      case 'importAssets':
        this.fileInputRef.current.click();
        break;
      case 'viewFileImports':
        const tab = this.props.source || 'distribution';
        this.props.history.push(`/catalogs/${tab}`);
        break;
      case 'exportAssets':
        const exportHref = `${config.serverUrl['metalMastodon']}ecs/api/asset_export/`;
        window.open(exportHref);
        break;
      default:
        // For linter
        break;
    }
  }

  createNewAsset() {
    const timeZone = this.props.rules.settings?.time_zone || 'UTC';
    this.props.actions.openModal('assetWizard', {
      mode: 'create',
      asset: {
        availability: 'Y',
        display_time_zone: timeZone,
        start_datetime: moment()
          .startOf('day')
          .format(moment.HTML5_FMT.DATETIME_LOCAL)
      },
      customFields: [...this.props.assets.keys]
    });
  }

  createAllowed(flags) {
    return !(flags && flags.isReadonly);
  }

  hasFilter() {
    const { browsedFilter: filter } = this.props.assets;
    return filter.assetClass || filter.customFilters?.length;
  }

  assetOverviewButtons(flags) {
    const { formatMessage } = this.props.intl;
    const newAsset = formatMessage({
      id: 'components.ecs.assetBrowser.header.newAsset'
    });

    const buildOption = value => ({
      className: value,
      label: formatMessage({
        id: `components.ecs.assetBrowser.header.${value}`
      }),
      value
    });
    const options = ['importAssets', 'viewFileImports', 'exportAssets'].map(
      buildOption
    );

    const searchPlaceholder = formatMessage({
      id: 'components.ecs.assetFilter.namePlaceholder'
    });

    return (
      <div className="asset-header-actions">
        <Input
          className="filter-by-name"
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search'
              }}
              name="search"
              category="utility"
            />
          }
          value={this.props.assets.browsedFilter['nameInput'] || ''}
          onChange={(evt, { value }) => {
            this.props.actions.updateAssetFilter({ nameInput: value });
            this.handleSearchByName(value);
          }}
          placeholder={searchPlaceholder}
          hasSpinner={this.props.loadContext.browser}
        />
        <Button
          className="filter-toggle"
          iconCategory="utility"
          iconName="filterList"
          iconSize="medium"
          onClick={() => this.setState({ filterOpen: !this.state.filterOpen })}
          variant={this.hasFilter() ? 'brand' : undefined}
        />
        <ButtonGroup variant="list" id="asset-button-list">
          <Button
            id="new-asset-button"
            disabled={!this.createAllowed(flags)}
            label={newAsset}
            onClick={this.createNewAsset.bind(this)}
          />
          <Dropdown
            assistiveText={{ icon: 'More Options' }}
            buttonVariant="icon"
            iconCategory="utility"
            iconName="down"
            iconVariant="border-filled"
            align="right"
            width="x-small"
            onSelect={this.onMenuSelect.bind(this)}
            options={options}
          />
        </ButtonGroup>
      </div>
    );
  }

  catalogToast() {
    if (this.props.toast.catalog) {
      const { formatMessage } = this.props.intl;
      const heading = formatMessage({
        id: 'components.ecs.assetBrowser.toast.heading'
      });
      const details = (
        <a href="/catalogs">
          {formatMessage({ id: 'components.ecs.assetBrowser.toast.details' })}
        </a>
      );
      return (
        <Toast
          labels={{ heading, details }}
          variant="info"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('catalog')}
        />
      );
    }

    return null;
  }

  assetToast() {
    const { formatMessage } = this.props.intl;
    if (this.props.toast.asset) {
      const heading = formatMessage(
        {
          id: `components.ecs.assetWizard.toast.${this.props.toast.asset.mode}`
        },
        { asset: this.props.toast.asset.key }
      );
      return (
        <Toast
          labels={{ heading }}
          variant="success"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('asset')}
        />
      );
    }

    return null;
  }

  toast() {
    return (
      <React.Fragment>
        {this.catalogToast()}
        {this.assetToast()}
      </React.Fragment>
    );
  }

  handleSearchByName(value) {
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      this.props.actions.updateAssetFilter({ name: value });
    }, 250);
  }

  handleBrowserAction(session) {
    return (action, item, disabled) => {
      switch (action) {
        case 'edit':
          // remove image_url so it shows as blank in the wizard
          const { image_url, ...asset } = item.asset;
          asset.original_image_url = image_url;
          asset.prevAsset = { ...asset };
          asset.start_datetime = asset.display_start_datetime;
          asset.end_datetime = asset.display_end_datetime;

          this.props.actions.openModal('assetWizard', {
            mode: 'edit',
            asset,
            disabled,
            customFields: [...this.props.assets.keys],
            step0: {
              completed: true
            },
            step1: {
              completed: true
            },
            step2: {
              completed: true
            }
          });
          break;
        case 'archive':
          if (disabled) {
            this.props.actions.openModal('archiveFallback', {
              asset: item.asset,
              action: 'archive'
            });
            break;
          }
          this.props.actions.archiveAsset(item.asset, 'browser', {
            cToken: session.cToken
          });
          break;
        case 'unarchive':
          this.props.actions.unarchiveAsset(item.asset, 'browser', {
            cToken: session.cToken
          });
          break;
        case 'enable':
        case 'disable':
          if (disabled) {
            this.props.actions.openModal('archiveFallback', {
              asset: item.asset,
              action: 'disable'
            });
            break;
          }
          item.asset['availability'] = action === 'enable' ? 'Y' : 'N';
          this.props.actions.editAsset(item.asset, {
            callback: () => {
              // don't have to worry about new/parent old parent since this is just
              // toggling availability
              if (item.asset.parent_id) {
                // refresh the parent
                this.props.actions.refreshAsset(item.asset.parent_id, {
                  context: 'browser'
                });
              }
            },
            context: 'browser',
            cToken: session.cToken
          });
          break;
        case 'toggleExpand':
          const currentExpandedAssets =
            this.props.assets.browsedFilter?.expandedAssets || [];
          let toggledAssets;
          if (currentExpandedAssets.includes(item.asset.uuid)) {
            toggledAssets = currentExpandedAssets.filter(
              uuid => item.asset.uuid !== uuid
            );
          } else {
            toggledAssets = [...currentExpandedAssets, item.asset.uuid];
          }
          this.props.actions.updateAssetFilter({
            expandedAssets: toggledAssets
          });
          break;
        default:
          break;
      }
    };
  }

  assetAttributeKeys() {
    return this.props.attributes.assetAttributes.map(e => e.name);
  }

  weatherAttributeKeys() {
    return this.props.attributes.weatherAttributes.map(e => e.name);
  }

  renderFilterPane() {
    if (!this.state.filterOpen) {
      return null;
    }
    return (
      <AssetFilterPane
        actions={this.props.actions}
        assetTypes={this.props.assets.types}
        attributes={this.props.attributes || {}}
        onClose={() => this.setState({ filterOpen: false })}
        filter={this.props.assets.browsedFilter}
      />
    );
  }

  // Need to insert children when parent is expanded
  flattenChildren(assetList) {
    const expandedAssets = [];
    assetList.forEach(asset => {
      expandedAssets.push(asset);
      if (
        this.props.assets.browsedFilter.expandedAssets?.includes(asset.uuid)
      ) {
        (asset.children || []).forEach(child => expandedAssets.push(child));
      }
    });

    return expandedAssets;
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  renderNoData(flags) {
    return (
      <Card
        className="asset-overview-card"
        header={this.assetOverviewHeader(0)}
        headerActions={this.assetOverviewButtons(flags)}
      >
        <NoDataScreen
          screen="assets"
          helpLink="https://help.salesforce.com/articleView?id=mc_pb_manage_einstein_content_selection_assets.htm&type=5"
        />
      </Card>
    );
  }

  renderTable(assets, paging, allowParentExpand, flags, session) {
    const sortColumn = this.props.assets.browsedFilter?.sortColumn || {
      property: 'updated_at',
      sortDirection: 'desc'
    };

    let lastSavedAsset = this.props.assets.lastSave?.asset;
    if (lastSavedAsset?.parent_id) {
      lastSavedAsset = assets.find(
        asset => asset.uuid === lastSavedAsset.parent_id
      );
    }
    const highlightKey = lastSavedAsset?.key;
    const keys = [
      ...(this.props.attributes.assetAttributes || []).map(attr => attr.name),
      ...(this.props.attributes.weatherAttributes || []).map(attr => attr.name)
    ];

    return (
      <Card
        className="asset-overview-card"
        header={this.assetOverviewHeader(paging)}
        headerActions={this.assetOverviewButtons(flags)}
      >
        <div className="asset-overview">
          <AssetBrowser
            assetClasses={this.props.assets.types}
            assets={assets}
            attributes={this.props.attributes}
            keys={keys}
            onAction={this.handleBrowserAction(session)}
            showActions={true}
            popoverOverflow={assets.length < 12}
            allowParentExpand={allowParentExpand}
            expandedRows={this.props.assets.browsedFilter?.expandedAssets}
            className="overview"
            rules={this.props.rules}
            hasMore={this.hasMore()}
            onLoadMore={() => this.handleLoadMore()}
            onScrollToTop={() => this.handleScrollToTop()}
            scrollToTop={this.state.scrollToTop}
            sortColumn={sortColumn}
            onSort={this.handleSort.bind(this)}
            sortable
            highlightKey={highlightKey}
            showTimestampColumns
          />
        </div>
      </Card>
    );
  }

  render() {
    const { isLoading } = this.props;
    const canRender = !isLoading && !!this.props.assets.search.browser?.assets;

    return withFlags(flags => {
      return withSession(session => {
        const filteredAssets = this.props.assets.search.browser?.assets || [];
        const assets = this.flattenChildren(filteredAssets);
        const allowParentExpand =
          assets.some(asset => asset.child_ids?.length) && flags.parentChild;
        const paging = this.props.assets.search.browser?.paging;
        return (
          <React.Fragment>
            {canRender && !this.props.assets.search.browser?.assets?.length
              ? this.renderNoData(flags)
              : this.renderTable(
                  assets,
                  paging,
                  allowParentExpand,
                  flags,
                  session
                )}
            {this.drawSpinner(!canRender && !this.state.loadedOnce)}
            <UploadCatalogHelper
              actions={this.props.actions}
              _ref={this.fileInputRef}
              catalogs={this.props.catalogs?.data?.catalogs || []}
              onUploadStart={() => this.props.actions.openToast('catalog')}
            />
            <ArchiveFallbackModal
              actions={this.props.actions}
              modals={this.props.modals}
              session={session}
            />
            <ToastContainer>{this.toast()}</ToastContainer>
            {this.renderFilterPane()}
          </React.Fragment>
        );
      });
    });
  }
}

AssetOverview.propTypes = {
  assets: PropTypes.object.isRequired,
  attributes: PropTypes.shape({
    assetAttributes: PropTypes.array,
    profileAttributes: PropTypes.array,
    weatherAttributes: PropTypes.array
  }),
  catalogs: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  source: PropTypes.oneOf(['settings', 'distribution']),
  modals: PropTypes.object,
  actions: PropTypes.object,
  location: PropTypes.object,
  toast: PropTypes.object
};

export default injectIntl(AssetOverview);
